// Dashboard de Ventas y Métricas (Sprint S5.1)
// Analytics agregados: resumen general, top clientes, clientes en riesgo,
// embudo conversión, ranking comerciales.
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { analyticsComercialesApi } from '../../services/api';
import DocumentoDrillDown from './DocumentoDrillDown';
import GraficoAnioVsAnio from './GraficoAnioVsAnio';
import {
  TrendingUp, TrendingDown, Users, Trophy, AlertTriangle,
  ArrowRight, Loader2, Receipt, Target, Minus, Clock, Database,
  ChevronDown, ChevronRight, Boxes,
} from 'lucide-react';

const fmtEur = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);
const fmtNum = (v) => new Intl.NumberFormat('es-ES').format(v || 0);
const fmtPct = (v) => v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${v}%`;

const KpiBig = ({ titulo, valor, sub, tone = 'default', icon: Icon, testid }) => {
  const tones = {
    default: 'bg-white border-ink-200',
    good: 'bg-emerald-50 border-emerald-200',
    warn: 'bg-amber-50 border-amber-200',
    bad: 'bg-red-50 border-red-200',
    brand: 'bg-brand-50 border-brand-200',
  };
  return (
    <Card className={`p-5 border ${tones[tone] || tones.default}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium text-ink-500 uppercase tracking-wider truncate">{titulo}</div>
          <div className="mt-2 text-3xl font-heading font-bold text-ink-900 leading-tight break-all">{valor}</div>
          {sub && <div className="mt-1 text-xs text-ink-500 truncate">{sub}</div>}
        </div>
        {Icon && <Icon className="w-6 h-6 text-ink-400 shrink-0" />}
      </div>
    </Card>
  );
};

export default function DashboardVentasMetricas() {
  const navigate = useNavigate();
  const [resumen, setResumen] = useState(null);
  const [topClientes, setTopClientes] = useState([]);
  const [riesgo, setRiesgo] = useState([]);
  const [embudo, setEmbudo] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [parcialidad, setParcialidad] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [familiaExpandida, setFamiliaExpandida] = useState(null);
  const hoy = new Date().toISOString().slice(0, 10);
  const inicioAno = `${new Date().getFullYear()}-01-01`;

  const [loading, setLoading] = useState(true);
  const [cobertura, setCobertura] = useState(null);
  const [empresa, setEmpresa] = useState('');
  const [desde, setDesde] = useState(inicioAno);
  const [hasta, setHasta] = useState(hoy);
  const [drillTipo, setDrillTipo] = useState(null); // 'oferta'|'pedido'|'albaran'|'factura'

  const cargarDatos = useCallback(async (emp, d, h) => {
    setLoading(true);
    try {
      const e = emp || undefined;
      const [r, tc, cr, emb, rk, cob, parc, fams] = await Promise.all([
        analyticsComercialesApi.resumenGeneral(e, d, h),
        analyticsComercialesApi.topClientes(10, e, d, h),
        analyticsComercialesApi.clientesRiesgo(90, 10, e, d, h),
        analyticsComercialesApi.embudoConversion(e, d, h),
        analyticsComercialesApi.rankingComerciales(10, e, d, h).catch(() => ({ items: [] })),
        analyticsComercialesApi.coberturaDatos().catch(() => null),
        analyticsComercialesApi.parcialidadClientes({ limit: 10, empresa: e, desde: d, hasta: h }).catch(() => ({ items: [] })),
        analyticsComercialesApi.porFamilia(e || undefined, d, h).catch(() => []),
      ]);
      setResumen(r);
      setTopClientes(tc.items || []);
      setRiesgo(cr.items || []);
      setEmbudo(emb);
      setRanking(rk.items || []);
      setCobertura(cob);
      setParcialidad(parc.items || []);
      setFamilias(Array.isArray(fams) ? fams : []);
      setFamiliaExpandida(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargarDatos('', inicioAno, hoy); }, [cargarDatos]); // eslint-disable-line

  const cambiarEmpresa = (e) => { setEmpresa(e); cargarDatos(e, desde, hasta); };
  const aplicarFechas = () => cargarDatos(empresa, desde, hasta);

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-ink-500 py-10" data-testid="dashboard-ventas-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Cargando métricas agregadas…
      </div>
    );
  }

  const variacion = resumen?.variacion_pct;
  const VarIcon = variacion === null ? Minus : variacion >= 0 ? TrendingUp : TrendingDown;
  const varTone = variacion === null ? 'default' : variacion >= 0 ? 'good' : 'bad';

  // Formatear fecha ISO "YYYY-MM-DD" → "DD/MM/AAAA"
  const fmtFecha = (iso) => {
    if (!iso) return '—';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  return (
    <div className="space-y-6" data-testid="dashboard-ventas-metricas">
      {/* Selector empresa + cobertura de datos */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-500">Empresa:</span>
          {['', 'platecsa', 'aguazor'].map(e => (
            <button
              key={e}
              onClick={() => cambiarEmpresa(e)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                empresa === e
                  ? e === 'platecsa' ? 'bg-blue-100 border-blue-300 text-blue-700'
                    : e === 'aguazor' ? 'bg-emerald-100 border-emerald-300 text-emerald-700'
                    : 'bg-slate-200 border-slate-300 text-slate-700'
                  : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
            >
              {e === '' ? 'Todas' : e.charAt(0).toUpperCase() + e.slice(1)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-2">
          <span className="text-xs font-medium text-slate-500">Período:</span>
          <input type="date" value={desde} onChange={e => setDesde(e.target.value)}
            className="border rounded px-2 py-1 text-xs text-slate-700" />
          <span className="text-xs text-slate-400">→</span>
          <input type="date" value={hasta} onChange={e => setHasta(e.target.value)}
            className="border rounded px-2 py-1 text-xs text-slate-700" />
          <button onClick={aplicarFechas}
            className="px-2 py-1 text-xs bg-brand-500 text-white rounded hover:bg-brand-600 disabled:opacity-50"
            disabled={loading}>
            {loading ? '…' : 'Aplicar'}
          </button>
        </div>

      {/* Indicador cobertura de datos */}
      {cobertura?.fecha_max && (
        <div className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border text-sm ${
          cobertura.alerta
            ? 'bg-amber-50 border-amber-200 text-amber-800'
            : 'bg-slate-50 border-slate-200 text-slate-600'
        }`}>
          {cobertura.alerta
            ? <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
            : <Database className="w-4 h-4 text-slate-400 shrink-0" />}
          <span>
            <span className="font-medium">Datos hasta: {fmtFecha(cobertura.fecha_max)}</span>
            {cobertura.fecha_min && (
              <span className="ml-2 text-xs opacity-70">
                (desde {fmtFecha(cobertura.fecha_min)} · {cobertura.total_facturas?.toLocaleString('es-ES')} facturas)
              </span>
            )}
          </span>
          {cobertura.alerta && (
            <span className="ml-auto text-xs font-medium text-amber-700 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> Datos desactualizados (&gt;30 días)
            </span>
          )}
        </div>
      )}
      </div>

      {/* KPIs principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <button onClick={() => setDrillTipo('factura')} className="text-left" data-testid="kpi-click-facturacion">
          <KpiBig
            titulo="Facturación YTD"
            valor={fmtEur(resumen?.facturacion_ytd)}
            sub={`${fmtNum(resumen?.n_facturas_ytd)} docs · incl. liquidaciones · click para explorar`}
            tone="brand"
            icon={Receipt}
            testid="kpi-fact-ytd-global"
          />
        </button>
        <KpiBig
          titulo="Variación vs año anterior"
          valor={fmtPct(variacion)}
          sub={`Mismo periodo anterior: ${fmtEur(resumen?.facturacion_ano_anterior_mismo_periodo)}`}
          tone={varTone}
          icon={VarIcon}
          testid="kpi-variacion-global"
        />
        <KpiBig
          titulo="Clientes activos YTD"
          valor={fmtNum(resumen?.n_clientes_activos_ytd)}
          sub="Con pedido o factura este año"
          icon={Users}
          testid="kpi-clientes-activos"
        />
        <KpiBig
          titulo="Clientes en riesgo"
          valor={fmtNum(riesgo.length)}
          sub="Sin compras >90 días"
          tone={riesgo.length > 0 ? 'warn' : 'good'}
          icon={AlertTriangle}
          testid="kpi-riesgo-global"
        />
      </div>

      {/* Embudo de conversión */}
      <Card className="p-5 border-ink-200 bg-white" data-testid="embudo-conversion">
        <h3 className="text-sm font-semibold text-ink-800 mb-4 flex items-center gap-2">
          <Target className="w-4 h-4 text-brand-500" /> Embudo de conversión · últimos 12 meses
          <span className="ml-auto text-xs text-ink-400 font-normal">Click en una etapa para explorar</span>
        </h3>
        <div className="flex items-center justify-between gap-2 overflow-x-auto">
          {embudo?.etapas?.map((etapa, i) => (
            <React.Fragment key={etapa.tipo}>
              <button
                onClick={() => setDrillTipo(etapa.tipo)}
                className="flex-1 min-w-[120px] text-center px-2 py-3 rounded-md hover:bg-ink-50 transition-colors cursor-pointer"
                data-testid={`embudo-etapa-${etapa.tipo}`}
              >
                <div className="text-xs font-medium text-ink-500 uppercase tracking-wider">{etapa.label}</div>
                <div className="mt-1 text-2xl font-heading font-bold text-ink-900">{fmtNum(etapa.n_documentos)}</div>
                <div className="text-xs text-ink-500">{fmtEur(etapa.importe_total)}</div>
                {etapa.conversion_vs_anterior_pct !== null && (
                  <Badge className={`mt-2 border ${etapa.conversion_vs_anterior_pct >= 80 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : etapa.conversion_vs_anterior_pct >= 50 ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                    {etapa.conversion_vs_anterior_pct}% conv.
                  </Badge>
                )}
              </button>
              {i < embudo.etapas.length - 1 && <ArrowRight className="text-ink-300 shrink-0 w-6 h-6" />}
            </React.Fragment>
          ))}
        </div>
        <p className="text-xs text-ink-400 mt-4 italic">
          * La conversión Pedidos→Ofertas &gt;100% indica pedidos directos sin oferta previa (casuística normal para clientes recurrentes).
        </p>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top clientes */}
        <Card className="p-5 border-ink-200 bg-white" data-testid="top-clientes">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-ink-800 flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" /> Top 10 clientes · YTD
            </h3>
            <span className="text-xs text-ink-400">Facturación</span>
          </div>
          <div className="space-y-1">
            {topClientes.map((c, i) => (
              <button
                key={`${c.id}-${i}`}
                onClick={() => c.id && navigate(`/clientes/${c.id}`)}
                disabled={!c.id}
                className="w-full text-left px-3 py-2 rounded hover:bg-ink-50 flex items-center gap-3 disabled:opacity-70 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                data-testid={`top-cliente-${i}`}
              >
                <div className="w-6 h-6 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-ink-800 truncate">{c.nombre}</div>
                  <div className="text-xs text-ink-500 truncate">
                    {c.provincia || '—'} · ERP {c.codigo_erp || '—'} · {c.n_facturas} fra.
                    {c.variacion_pct !== null && (
                      <span className={`ml-2 ${c.variacion_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {fmtPct(c.variacion_pct)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-sm font-semibold text-ink-900 shrink-0">{fmtEur(c.facturacion_ytd)}</div>
              </button>
            ))}
            {!topClientes.length && <p className="text-sm text-ink-400 italic px-3 py-2">Sin datos de facturación este año.</p>}
          </div>
        </Card>

        {/* Clientes en riesgo */}
        <Card className="p-5 border-ink-200 bg-white" data-testid="clientes-riesgo">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-ink-800 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" /> Clientes en riesgo · sin compras &gt;90d
            </h3>
            <span className="text-xs text-ink-400">Top 10 por valor</span>
          </div>
          <div className="space-y-1">
            {riesgo.map((c, i) => (
              <button
                key={`${c.id}-${i}`}
                onClick={() => c.id && navigate(`/clientes/${c.id}`)}
                disabled={!c.id}
                className="w-full text-left px-3 py-2 rounded hover:bg-ink-50 flex items-center gap-3 disabled:opacity-70 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                data-testid={`riesgo-cliente-${i}`}
              >
                <div className="w-6 h-6 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-xs font-bold shrink-0">!</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-ink-800 truncate">{c.nombre}</div>
                  <div className="text-xs text-ink-500 truncate">
                    {c.provincia || '—'} · Última compra {c.ultima_compra || '—'} · Histórico {fmtEur(c.historico_facturado)}
                  </div>
                </div>
                <Badge className="bg-red-50 border border-red-200 text-red-700 shrink-0">{c.dias_sin_comprar}d</Badge>
              </button>
            ))}
            {!riesgo.length && <p className="text-sm text-emerald-600 italic px-3 py-2">✓ Ningún cliente en riesgo.</p>}
          </div>
        </Card>
      </div>

      {/* Ranking comerciales (solo admin/dir_comercial) */}
      {ranking.length > 0 && (
        <Card className="p-5 border-ink-200 bg-white" data-testid="ranking-comerciales">
          <h3 className="text-sm font-semibold text-ink-800 mb-3 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-violet-500" /> Ranking comerciales · YTD
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-500 border-b border-ink-200">
                <th className="py-2 pr-2 font-medium">#</th>
                <th className="py-2 pr-2 font-medium">Comercial</th>
                <th className="py-2 pr-2 font-medium text-right">Facturación YTD</th>
                <th className="py-2 pr-2 font-medium text-right">Clientes</th>
                <th className="py-2 pr-2 font-medium text-right">Pedidos</th>
              </tr>
            </thead>
            <tbody>
              {ranking.map((c, i) => (
                <tr key={c.comercial_id} className="border-b border-ink-100 last:border-0">
                  <td className="py-2 pr-2 text-ink-500">{i + 1}</td>
                  <td className="py-2 pr-2 font-medium text-ink-800">{c.nombre}</td>
                  <td className="py-2 pr-2 text-right">{fmtEur(c.facturacion_ytd)}</td>
                  <td className="py-2 pr-2 text-right">{c.n_clientes}</td>
                  <td className="py-2 pr-2 text-right">{c.n_pedidos}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* B6 — Análisis año vs. año */}
      <GraficoAnioVsAnio empresaGlobal={empresa} />

      {/* Tasa de parcialidad por cliente */}
      {parcialidad.length > 0 && (
        <Card className="p-5">
          <h3 className="font-semibold text-ink-900 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Top clientes por parcialidad de pedidos
          </h3>
          <table className="w-full text-sm">
            <thead className="border-b text-slate-500 text-xs">
              <tr>
                <th className="pb-2 font-medium text-left">Cliente</th>
                <th className="pb-2 font-medium text-right">Tasa %</th>
                <th className="pb-2 font-medium text-right">Parciales / Total</th>
                <th className="pb-2 font-medium text-right">Importe retenido</th>
                <th className="pb-2 font-medium text-right">Última incidencia</th>
              </tr>
            </thead>
            <tbody>
              {parcialidad.map((c, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="py-2 text-slate-700 max-w-[220px] truncate text-xs" title={c.cliente_nombre_erp}>
                    {c.cliente_nombre_erp || c.cliente_codigo_erp || '—'}
                  </td>
                  <td className="py-2 text-right">
                    <span className={`font-bold text-xs ${c.tasa_parcialidad >= 50 ? 'text-red-600' : c.tasa_parcialidad >= 20 ? 'text-amber-600' : 'text-slate-600'}`}>
                      {c.tasa_parcialidad}%
                    </span>
                  </td>
                  <td className="py-2 text-right text-xs text-slate-500">{c.pedidos_parciales} / {c.total_pedidos}</td>
                  <td className="py-2 text-right text-xs font-mono text-amber-700">{fmtEur(c.importe_retenido)}</td>
                  <td className="py-2 text-right text-xs text-slate-500">{c.ultima_incidencia || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Análisis por Familia */}
      {familias.length > 0 && (() => {
        const maxImporte = Math.max(...familias.map((f) => f.importe_periodo), 1);
        return (
          <Card className="p-5 border-ink-200 bg-white" data-testid="analisis-por-familia">
            <h3 className="text-sm font-semibold text-ink-800 mb-4 flex items-center gap-2">
              <Boxes className="w-4 h-4 text-brand-500" /> Análisis por familia de producto
              <span className="ml-auto text-xs text-ink-400 font-normal">Click para expandir subfamilias</span>
            </h3>
            <div className="space-y-1">
              {familias.map((f) => {
                const pct = Math.round((f.importe_periodo / maxImporte) * 100);
                const isOpen = familiaExpandida === f.familia_id;
                const VIcon = f.variacion_pct === null ? Minus : f.variacion_pct >= 0 ? TrendingUp : TrendingDown;
                const vCls = f.variacion_pct === null ? 'text-ink-400' : f.variacion_pct >= 0 ? 'text-emerald-600' : 'text-red-600';
                const maxSub = f.subfamilias?.length ? Math.max(...f.subfamilias.map((s) => s.importe), 1) : 1;
                return (
                  <div key={f.familia_id}>
                    <button
                      onClick={() => setFamiliaExpandida(isOpen ? null : f.familia_id)}
                      className="w-full text-left px-3 py-2 rounded hover:bg-ink-50 transition-colors"
                      data-testid={`familia-row-${f.familia_id}`}
                    >
                      <div className="flex items-center gap-3">
                        {isOpen
                          ? <ChevronDown className="w-3.5 h-3.5 text-ink-400 shrink-0" />
                          : <ChevronRight className="w-3.5 h-3.5 text-ink-400 shrink-0" />}
                        <div className="w-36 shrink-0 text-xs font-medium text-ink-700 truncate" title={f.familia_nombre}>
                          {f.familia_id} — {f.familia_nombre}
                        </div>
                        <div className="flex-1 h-4 bg-ink-100 rounded-full overflow-hidden">
                          <div className="h-full bg-brand-400 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="w-28 text-right text-sm font-semibold text-ink-900 shrink-0">{fmtEur(f.importe_periodo)}</div>
                        <div className={`w-16 text-right text-xs shrink-0 flex items-center justify-end gap-0.5 ${vCls}`}>
                          <VIcon className="w-3 h-3" />{fmtPct(f.variacion_pct)}
                        </div>
                        <div className="w-16 text-right text-xs text-ink-400 shrink-0">{f.clientes_activos} cli.</div>
                      </div>
                    </button>
                    {isOpen && f.subfamilias?.length > 0 && (
                      <div className="ml-8 mt-1 mb-2 space-y-1 border-l-2 border-ink-100 pl-3">
                        {f.subfamilias.map((s) => {
                          const sPct = Math.round((s.importe / maxSub) * 100);
                          const SVIcon = s.variacion_pct === null ? Minus : s.variacion_pct >= 0 ? TrendingUp : TrendingDown;
                          const sCls = s.variacion_pct === null ? 'text-ink-400' : s.variacion_pct >= 0 ? 'text-emerald-600' : 'text-red-600';
                          return (
                            <div key={s.subfamilia_id} className="flex items-center gap-3 px-2 py-1 rounded hover:bg-ink-50">
                              <div className="w-36 shrink-0 text-xs text-ink-500 truncate" title={s.subfamilia_nombre}>
                                {s.subfamilia_id} — {s.subfamilia_nombre}
                              </div>
                              <div className="flex-1 h-2.5 bg-ink-100 rounded-full overflow-hidden">
                                <div className="h-full bg-brand-300 rounded-full" style={{ width: `${sPct}%` }} />
                              </div>
                              <div className="w-28 text-right text-xs font-medium text-ink-700 shrink-0">{fmtEur(s.importe)}</div>
                              <div className={`w-16 text-right text-xs shrink-0 flex items-center justify-end gap-0.5 ${sCls}`}>
                                <SVIcon className="w-3 h-3" />{fmtPct(s.variacion_pct)}
                              </div>
                              <div className="w-16 shrink-0" />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })()}

      <DocumentoDrillDown
        open={!!drillTipo}
        onOpenChange={(o) => { if (!o) setDrillTipo(null); }}
        tipoInicial={drillTipo}
      />
    </div>
  );
}
